// app/(app)/customers/index.tsx
import CustomerCard from "@/components/customer/CustomerCard";
import { Button, Input } from "@/components/default/forms";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { locationApi } from "@/services/locationApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearError, fetchCustomers } from "@/store/slices/customerSlice";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function CustomersListScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { customers, pagination, loading, error } = useAppSelector(
    (state) => state.customers,
  );
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [areas, setAreas] = useState<{ id: number; name: string }[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const limit = 10;

  // Load areas on mount
  useEffect(() => {
    loadAreas();
  }, []);

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Fetch customers whenever search text or area changes
  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
    dispatch(
      fetchCustomers({
        page: 1,
        limit,
        search: searchText,
        areaId: selectedAreaId ? parseInt(selectedAreaId) : undefined,
      }),
    );
  }, [searchText, selectedAreaId]);

  const loadMore = () => {
    if (!loading && pagination.hasNextPage) {
      const nextPage = pagination.currentPage + 1;
      dispatch(
        fetchCustomers({
          page: nextPage,
          limit,
          search: searchText,
          areaId: selectedAreaId ? parseInt(selectedAreaId) : undefined,
        }),
      );
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    dispatch(
      fetchCustomers({
        page: 1,
        limit,
        search: searchText,
        areaId: selectedAreaId ? parseInt(selectedAreaId) : undefined,
      }),
    ).finally(() => setRefreshing(false));
  };

  const handleSearch = () => {
    // searchText already triggers useEffect
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      padding: spacing.lg,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    title: { fontSize: 24, fontWeight: "600", color: colors.text },
    filters: {
      marginBottom: spacing.md,
      gap: spacing.sm,
    },
    searchContainer: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
    },
    searchInput: { flex: 1 },
    listContent: { paddingBottom: spacing.xl },
    emptyContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: spacing.xxl,
    },
    emptyText: { fontSize: 16, color: colors.textLight },
    footerLoader: { paddingVertical: spacing.lg, alignItems: "center" },
  });

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Customers</Text>
        <Button
          title="+ Add"
          onPress={() => router.push("/(app)/customers/add")}
          variant="primary"
          containerStyle={{ paddingHorizontal: 16, paddingVertical: 8 }}
        />
      </View>

      <View style={styles.filters}>
        {/* Search by name/email */}
        <View style={styles.searchContainer}>
          <Input
            placeholder="Search by name or phone"
            value={searchText}
            onChangeText={setSearchText}
            onSubmitEditing={handleSearch}
            containerStyle={styles.searchInput}
          />
          <Button
            title="Search"
            onPress={handleSearch}
            variant="outline"
            containerStyle={{ paddingHorizontal: 12 }}
          />
        </View>

        {/* Search by Area */}
        <SearchableSelect
          label="Area / Route"
          placeholder="All areas"
          options={areas.map((a) => ({
            label: a.name,
            value: a.id.toString(),
          }))}
          value={selectedAreaId}
          onValueChange={(val) => setSelectedAreaId(val)}
          loading={loadingAreas}
          containerStyle={{ marginBottom: 0 }}
          clearable
        />
      </View>

      <FlatList
        data={customers}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => (
          <CustomerCard customer={item} onRefresh={handleRefresh} />
        )}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        ListEmptyComponent={
          !loading ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No customers found</Text>
            </View>
          ) : null
        }
        ListFooterComponent={
          loading && customers.length > 0 ? (
            <View style={styles.footerLoader}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          ) : null
        }
      />
    </View>
  );
}
